/**
 * Build a WebSocket URL for the molecule.dev API's `/ws/*` endpoints.
 *
 * ONE derivation for every socket the workspace opens (files, presence, LSP,
 * terminal). Origin precedence:
 *   1. `VITE_WS_ORIGIN` — an EXPLICIT socket origin. Production needs this: the
 *      app is served from www.molecule.dev (Netlify, static) while the API is
 *      api.molecule.dev (Fly), and Netlify cannot proxy a WebSocket upgrade —
 *      so a same-origin `wss://www.molecule.dev/ws/...` fails (verified at
 *      launch, 2026-08-08: the presence socket errored). WS auth is a `?token=`
 *      query param, NOT a cookie, so pointing straight at the cross-origin API
 *      is safe (no first-party-cookie dependency, unlike the HTTP /api proxy).
 *   2. `VITE_API_URL` — when the API is on a different origin but reachable.
 *   3. same-origin (`window.location`) — dev, where vite proxies `/ws`.
 *
 * @param path - The `/ws/...` path (e.g. `/ws/projects/<id>/terminal`).
 * @param token - Optional auth token appended as `?token=`.
 * @returns The fully-qualified `ws(s)://` URL.
 */
export const buildWsUrl = (path: string, token?: string | null): string => {
  const wsOrigin = import.meta.env.VITE_WS_ORIGIN as string | undefined
  const apiUrl = import.meta.env.VITE_API_URL as string | undefined
  const base = wsOrigin
    ? new URL(wsOrigin)
    : apiUrl
      ? new URL(apiUrl, window.location.href)
      : window.location
  const protocol = base.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${base.host}${path}${token ? `?token=${encodeURIComponent(token)}` : ''}`
}
